/**
 * The **seed overlay** — LOADED, not hardcoded: {@link loadOverlay} fed the authored
 * {@link SEED_ENTRIES} (`seedEntries.ts`) produces the real id-keyed, `{VAL}`-bindable overlay,
 * the same artifact a pack produces. The gather seam below (`seedHandlers`/`seedModifiers`)
 * delegates to the loader's `gatherHandlers`/`gatherModifiers` off that overlay, so the buses
 * consume one shape regardless of where the entries came from.
 *
 * The seed-entry *content* (the statuses, the tags) lives in `seedEntries.ts`; this module is
 * the load + gather seam.
 */

import type { EncounterState, DamageType } from "@lancer-vtt/domain";
import { hasStatus, isCharacter, sideOf, worldFor } from "@lancer-vtt/domain";
import type { ImmunityScope, OverlayEntry } from "./atoms";
import type { Handler } from "./triggerBus";
import type { BoundModifier, ModifierContributionSum } from "./modifiers";
import { foldModifiers } from "./modifiers";
import type { AttackEvent, PredicateContext, TriggerEvent } from "./predicate";
import {
  loadOverlay,
  gatherHandlers,
  gatherModifiers,
  gatherConferredModifiers,
  gatherModifiersAt,
  gatherWeaponModifiers,
  gatherPilotHandlers,
  gatherPilotModifiers,
  gatherSystemModifiers,
  type LoadedOverlay,
} from "./loader";
import { SEED_ENTRIES } from "./seedEntries";

// Re-export the authored (intrinsics-only) entries so importers (tests, the room) reach them
// through this seam. The migrated content entries live in `packs/core-rulebook` — see
// `seedEntries.ts`'s header.
export {
  IMPAIRED,
  LOCK_ON,
  BOLSTER,
  SHREDDED,
  EXPOSED,
  BRACED,
  STUNNED,
  IMMOBILIZED,
  FLYING,
  GRAPPLED,
  SLOWED,
  PRONE,
  JAMMED,
  ENGAGED,
  DANGER_ZONE,
  DOWN_AND_OUT,
  SHUT_DOWN,
  BIOLOGICAL,
  HIDDEN,
  INVISIBLE,
  INTANGIBLE,
  INVADE,
  RAM,
  GRAPPLE,
  LOCK_ON_ACTION,
  BOLSTER_ACTION,
  SCAN,
  TURN_START_FIXTURE,
  TAG_AP,
  TAG_ACCURATE,
  TAG_RELIABLE,
  TAG_OVERKILL,
  TAG_LIMITED,
  TAG_KNOCKBACK,
  SEED_ENTRIES,
} from "./seedEntries";

/**
 * The **loaded** seed overlay — built once from {@link SEED_ENTRIES} via the loader.
 * Status/item entries are bound (no `{VAL}`); tag templates keep their placeholders (bound
 * per-weapon at gather).
 */
export const SEED_OVERLAY: LoadedOverlay = loadOverlay(SEED_ENTRIES);

// ── the gather seam (preserved interface; delegates to the loader) ─────────────────

/**
 * Gather the **triggered** handlers active for an attack. Delegates to the loader's
 * {@link gatherHandlers} off the loaded overlay against the firing weapon's id + its bound tags.
 *
 * ONE call gathers the whole `"attack"` family — declared/hit/crit/miss clauses from this
 * weapon all come back together, and the caller's single `fireTrigger("attack", …)`
 * discriminates them by `match` (hit/crit/miss/phase) at fire time. Gathering per-outcome
 * instead is how a clause double-fires.
 */
export type SeedHandlersArgs = {
  state: EncounterState;
  attackerId: string;
  weapon: string | { id: string; tags?: { id: string; val?: number }[] } | undefined;
  overlay?: LoadedOverlay;
};

export function seedHandlers(args: SeedHandlersArgs): Handler[] {
  const { state, attackerId, weapon, overlay = SEED_OVERLAY } = args;
  const w = typeof weapon === "string" ? { id: weapon } : weapon;
  return gatherHandlers({ state, attackerId, weapon: w, overlay, family: "attack" });
}

/**
 * Gather the **modifier** contributions active on a combatant. Delegates to the loader's
 * {@link gatherModifiers} off the loaded overlay,
 * matching the combatant's active statuses to overlay entries. (Weapon-tag modifiers —
 * Accurate, etc. — gather against the attack's weapon via {@link seedWeaponModifiers},
 * not a status.)
 */
export function seedModifiers(
  state: EncounterState,
  combatantId: string,
  overlay: LoadedOverlay = SEED_OVERLAY,
): BoundModifier[] {
  return gatherModifiers(state, combatantId, overlay);
}

/**
 * Gather the attacker-side **weapon-tag** modifiers for an attack — the `{VAL}` tag templates
 * bound per-weapon (Accurate {VAL}, etc.). These fold into the attacker side of the to-hit math
 * alongside the attacker's status modifiers.
 */
export function seedWeaponModifiers(
  attackerId: string,
  weapon: { tags?: { id: string; val?: number }[] } | undefined,
  overlay: LoadedOverlay = SEED_OVERLAY,
): BoundModifier[] {
  return gatherWeaponModifiers(attackerId, weapon, overlay);
}

/**
 * Gather the **triggered** handlers a combatant's frozen **pilot effects** contribute (the
 * active talent/core-bonus path). Delegates to the loader's {@link gatherPilotHandlers} off the
 * loaded overlay against the carrier's frozen `base.pilotEffectIds`. Same `Handler[]` shape
 * `seedHandlers` returns, so the resolver merges the two lists into ONE trigger-bus firing.
 *
 * Gathers the whole `"attack"` family in one call — see {@link seedHandlers}'s own doc.
 */
export type SeedPilotHandlersArgs = {
  carrierId: string;
  pilotEffectIds: readonly string[] | undefined;
  overlay?: LoadedOverlay;
};

export function seedPilotHandlers(args: SeedPilotHandlersArgs): Handler[] {
  const { carrierId, pilotEffectIds, overlay = SEED_OVERLAY } = args;
  return gatherPilotHandlers({ carrierId, pilotEffectIds, overlay, family: "attack" });
}

/**
 * Gather the **modifier** contributions a combatant's frozen **pilot effects** grant (a
 * conditional talent/core-bonus boon). Delegates to the loader's {@link gatherPilotModifiers}
 * off the loaded overlay against the carrier's frozen `base.pilotEffectIds`. Same
 * `BoundModifier[]` shape the status/weapon-tag gathers return, so the resolver folds them
 * through the same `foldModifiers` path. Takes an options object so an optional `partner` id
 * threads through to {@link gatherPilotModifiers} — see that function's doc for the
 * `partner`-binding rationale (`pilot_modifier_partner_binding`, t_brawler:1).
 */
export type SeedPilotModifiersArgs = {
  carrierId: string;
  pilotEffectIds: readonly string[] | undefined;
  overlay?: LoadedOverlay;
  partner?: string;
};

export function seedPilotModifiers(args: SeedPilotModifiersArgs): BoundModifier[] {
  const { carrierId, pilotEffectIds, overlay = SEED_OVERLAY, partner } = args;
  return gatherPilotModifiers({ carrierId, pilotEffectIds, overlay, partner });
}

/**
 * Gather the **modifier** contributions a mech's frozen **equipped systems** grant
 * (ms_stable_structure's passive Prone/Knockback save boon). Delegates to the loader's
 * {@link gatherSystemModifiers} off the loaded overlay against the carrier's frozen
 * `base.equippedSystems`. Same `BoundModifier[]` shape the status/pilot-effect/weapon-tag
 * gathers return, so callers fold it through the same `foldModifiers` path.
 */
export function seedSystemModifiers(
  state: EncounterState,
  combatantId: string,
  overlay: LoadedOverlay = SEED_OVERLAY,
): BoundModifier[] {
  return gatherSystemModifiers(state, combatantId, overlay);
}

/** True iff `combatantId` carries a status the seed overlay maps to a modifier (test helper). */
export function carriesSeedModifier(state: EncounterState, combatantId: string, status: string): boolean {
  const c = state.combatants[combatantId];
  return c ? hasStatus(c.statuses, status) : false;
}

/** Args for {@link immuneToTechAction} (max-params-3 discipline, `CODE_STYLE.md`). */
export type ImmuneToTechActionArgs = {
  state: EncounterState;
  targetId: string;
  actionId: string;
  overlay?: LoadedOverlay;
};

/**
 * The **folded per-action tech-immunity predicate**. Gathers `targetId`'s active overlay
 * modifiers off every carrier vector and folds them (immunity is a carrier-self trait,
 * side-agnostic — see {@link foldModifiers}); the target is blocked from `actionId` iff ANY
 * gathered `tech` scope does not except it. Shut Down carries `{kind:"tech"}` (no `except` →
 * blocks every action id); Biological carries `{kind:"tech", except:["act_scan","act_lock_on"]}`.
 * A plain boolean can't express both shapes in one flag, which is why this is a fold rather than
 * a stored flag.
 *
 * **Reads the WIDE composed fold ({@link foldWide})** — the immunity-family widening
 * (`immunity-foldwide`) moved this read-site, {@link statusBlockedByImmunity} and {@link
 * hasBlanketImmunity} onto the same `gatherModifiersAt` composition Gap B (`decisions/0171`)
 * had already moved {@link foldedResistance}/{@link immuneToDamage} to, so all four `immunity`
 * read-sites now fold identically. A tech-scoped grant is therefore reachable from a system,
 * pilot-effect or NPC-feature carrier, not statuses alone. No attacker-relative tech-immunity
 * grant is fielded, so the fold is called self-referentially (`attacker = targetId`) — identical
 * behavior to before for every existing carrier.
 *
 * Read at the tech-legality gate in `encounterRoom.resolveIntent` (beside the Hidden/range
 * gates) and by `quickTech.ts`'s chooser filter. The `damage`/`status` scopes are gathered into
 * the same fold but read by the sibling predicates below, not here.
 */
export function immuneToTechAction(args: ImmuneToTechActionArgs): boolean {
  const { state, targetId, actionId, overlay = SEED_OVERLAY } = args;
  const folded = foldWide({ state, targetId, overlay, attacker: targetId });
  return folded.immunity.some((scope) => scope.kind === "all" || scope.kind === "damage_and_effects" || (scope.kind === "tech" && !(scope.except ?? []).includes(actionId)));
}

/**
 * Fold `targetId`'s active overlay modifiers off the COMPOSED walk (status + pilot/frame/gear/
 * npc-feature + system) — the ONE fold behind every carrier-self Immunity/Resistance predicate
 * in this file. Gap B (`npc-features-fielded-roster` Phase 2, `decisions/0171`) introduced it
 * for {@link foldedResistance}/{@link immuneToDamage}; the `immunity-foldwide` widening moved
 * the remaining three ({@link immuneToTechAction}/{@link statusBlockedByImmunity}/{@link
 * hasBlanketImmunity}) onto it and retired the narrow, status-only `foldSelf` it used to have as
 * a sibling. `attacker` is threaded into the context so a condition can read a fact about the
 * ATTACK (`is_closest_hostile`/`range_to`), not just about the carrier itself — callers with no
 * real attacker in scope (every read-site but {@link foldedResistance}) pass `targetId` again,
 * the same self-referential shape the retired `foldSelf` always used.
 *
 * **Takes an args object, not four positional params** — `CODE_STYLE.md`'s hard-gated oxlint
 * `max-params` (3) rule; this would have been a 4th positional param (`state`/`targetId`/
 * `overlay` already occupy the first three, same trap Phase 1's `combatantEffectIds` hit).
 */
function foldWide(args: WideFoldArgs): ModifierContributionSum {
  return foldModifiers(gatherModifiersAt({ state: args.state, carrierId: args.targetId, overlay: args.overlay }), "target", wideContext(args));
}

type WideFoldArgs = { state: EncounterState; targetId: string; overlay: LoadedOverlay; attacker: string | undefined; event?: AttackEvent };

function wideContext(args: WideFoldArgs): PredicateContext {
  const { state, targetId, attacker, event } = args;
  return { state, actors: { self: targetId, target: targetId, attacker }, ...(event ? { event } : {}) };
}

/** Args for {@link foldedResistance} (max-params-3 discipline, `CODE_STYLE.md`). */
export type FoldedResistanceArgs = {
  state: EncounterState;
  targetId: string;
  /**
   * The REAL attacker of the hit this Resistance is being checked against — never `targetId`
   * itself (`decisions/0171` pin: PDS's `is_closest_hostile` and Siege Armor's `range_to` are
   * both facts about the attacker, and a context that silently substituted the target would make
   * both predicates permanently true or permanently false regardless of who actually attacked).
   * Required, not defaulted, so a future call site can't forget to thread it.
   *
   * **`null` when the damage has no source character at all** — dangerous terrain, the burn
   * tick, GM-adjudicated damage. The fold then binds no `attacker`, so every attacker-relative
   * predicate (`is_closest_hostile`, `range_to`, `attacker_is`, `hostile_to`/`adjacent_to` on
   * `attacker`) reads `false` through `resolveActorId`'s absent-means-false convention: PDS and
   * Siege Armor never fold, an unconditional grant (Argonaut Shield, Stampede Defense) still
   * does. A clause that NEGATES an attacker-relative predicate would read `true` here; none is
   * authored today.
   */
  attackerId: string | null;
  /**
   * **Is this damage delivered by an attack?** `true` on the attack spine (the hit, the protector's
   * share, the Reliable floor, the forecast and the AI's EV) and for an `extra_damage` rider fired
   * inside an attack's resolution (Cluster Munitions); `false` at every non-attack site (a mine, a
   * bulldoze run-over, Trample, dangerous terrain, burn, GM damage, the meltdown blast).
   *
   * `true` binds a bare attack-family event (`{ family: "attack" }`) onto the fold's context, so a
   * resistance modifier gated `match: {}` — "damage from attacks" (Siege Armor, Core p.300) — folds
   * only here. The event names the family and nothing else: a further `match` fact (`ranged`,
   * `hit`) reads undefined and never holds on a resistance modifier. `false` binds no event, so a
   * `match`-gated modifier never folds. Required, like `attackerId`, so no site can forget it.
   */
  fromAttack: boolean;
  dtype: DamageType;
  overlay?: LoadedOverlay;
};

/** The event {@link foldedResistance} binds when {@link FoldedResistanceArgs.fromAttack} is set. */
const ATTACK_DAMAGE_EVENT: AttackEvent = { family: "attack" };

/**
 * The **folded Resistance predicate** — does `targetId` hold Resistance to `dtype` right now,
 * off its active overlay modifiers (Argonaut Shield's `resistance: "all"` grant, a scoped grant,
 * or — since Gap B — an attacker-relative NPC-feature grant like Point-Defense Shield or Siege
 * Armor)? The mitigate read-site UNIFIES this with Brace's built-in `resistant` flag:
 * `resistant = builtin outcome ∨ foldedResistance(...)` — Brace stays a permanent built-in,
 * this is the content half of the same read.
 *
 * **Reads the WIDE composed fold ({@link foldWide}), not a narrow status-only gather** — Gap B's
 * whole point: a System-typed (Point-Defense Shield) or Trait-typed-riding-
 * `PILOT_EFFECT_VECTOR` (Stampede Defense, Siege Armor) NPC feature can now grant Resistance,
 * where before only a status could. This is the ONE read-site that threads a REAL, distinct
 * attacker; the four `immunity` siblings all fold self-referentially.
 */
export function foldedResistance(args: FoldedResistanceArgs): boolean {
  const { state, targetId, attackerId, fromAttack, dtype, overlay = SEED_OVERLAY } = args;
  return resistanceCovers(foldWide({ state, targetId, overlay, attacker: attackerId ?? undefined, ...(fromAttack ? { event: ATTACK_DAMAGE_EVENT } : {}) }), dtype);
}

/** Args for {@link immuneToDamage} (max-params-3 discipline, `CODE_STYLE.md`). */
export type ImmuneToDamageArgs = {
  state: EncounterState;
  targetId: string;
  dtype: DamageType;
  overlay?: LoadedOverlay;
};

/**
 * The **folded damage-scope Immunity predicate** — is `targetId` immune to `dtype` damage right
 * now (Stay of Execution's "Immunity to all damage … from external sources")? Gathers
 * `targetId`'s active overlay modifiers and folds their `immunity` scopes (carrier-self,
 * side-agnostic — see {@link foldModifiers}); blocked iff ANY gathered `damage` scope either
 * omits `types` (immune to everything) or names `dtype`. RAW (p.68): a no-op, never a to-hit/
 * damage penalty — the mitigate read-site suppresses the `hp_delta` step entirely rather
 * than mitigating to a mere zero.
 *
 * **Reads the WIDE composed fold ({@link foldWide})** — Gap B widened this read-site alongside
 * {@link foldedResistance} (`decisions/0171`/`decisions/0166` name those two as the proven
 * consumers); the `immunity-foldwide` widening then brought the other three `immunity` read-sites
 * onto the same fold, so the facet no longer has per-scope reachability seams. No attacker-
 * relative Immunity grant is fielded by this roster, so the fold is called self-referentially
 * (`attacker = targetId`) — identical behavior to before for every existing carrier, and ready
 * for an attacker-relative Immunity grant should one ever ship.
 */
export function immuneToDamage(args: ImmuneToDamageArgs): boolean {
  const { state, targetId, dtype, overlay = SEED_OVERLAY } = args;
  return immuneCovers(foldWide({ state, targetId, overlay, attacker: targetId }), dtype);
}

function immuneCovers(folded: ModifierContributionSum, dtype: DamageType): boolean {
  return folded.immunity.some((scope) => scope.kind === "all" || scope.kind === "damage_and_effects" || (scope.kind === "damage" && (!scope.types || scope.types.includes(dtype))));
}

function resistanceCovers(folded: ModifierContributionSum, dtype: DamageType): boolean {
  return folded.resistance.includes("all") || folded.resistance.includes(dtype);
}

/** Args for {@link damageGrantCaps}: a {@link foldedResistance} read, and which grant the damage used. */
export type DamageGrantCapsArgs = FoldedResistanceArgs & { grant: "resistance" | "immunity" };

/**
 * **The capped grant a damage packet spends** (`event-engine-damage` R7, BC-077): the cap of the
 * first capped modifier granting `targetId` Resistance to `dtype` (or, for `immunity`, damage
 * Immunity), read with the same fold as {@link foldedResistance} / {@link immuneToDamage}. None
 * when an uncapped grant covers the damage too: the target needn't spend a limited use on what it
 * resists anyway. The caller asks only when the grant actually applied to the packet.
 */
export function damageGrantCaps(args: DamageGrantCapsArgs): { carrier: string; key: string }[] {
  const { state, targetId, attackerId, fromAttack, dtype, overlay = SEED_OVERLAY, grant } = args;
  const wide: WideFoldArgs =
    grant === "immunity" ? { state, targetId, overlay, attacker: targetId } : { state, targetId, overlay, attacker: attackerId ?? undefined, ...(fromAttack ? { event: ATTACK_DAMAGE_EVENT } : {}) };
  const ctx = wideContext(wide);
  let capped: { carrier: string; key: string } | undefined;
  for (const mod of gatherModifiersAt({ state, carrierId: targetId, overlay })) {
    const one = foldModifiers([mod], "target", ctx);
    if (!(grant === "immunity" ? immuneCovers(one, dtype) : resistanceCovers(one, dtype))) continue;
    const cap = one.consumedResourceCaps[0];
    if (cap === undefined) return [];
    capped ??= cap;
  }
  return capped !== undefined ? [capped] : [];
}

/** Args for {@link statusBlockedByImmunity} (max-params-3 discipline, `CODE_STYLE.md`). */
export type StatusBlockedByImmunityArgs = {
  state: EncounterState;
  targetId: string;
  name: string;
  overlay?: LoadedOverlay;
};

/**
 * The **folded status-scope Immunity predicate** — is `targetId` immune to the NAMED status
 * `name` being applied at all (a p.328-style "IMMUNITY to IMMOBILIZED, STUNNED and PRONE"
 * grant, or Stay of Execution's "immunity to … effects from external sources")? Consulted
 * BEFORE emitting a `status_add` step — a blocked add surfaces a note, never a step;
 * the block never reaches the reducer (grounded steps stay pure). Name comparison is
 * case-/whitespace-insensitive, mirroring the overlay's own status-lookup normalization
 * (`statusOverlayEntry`, `loader.ts`) so a GM-typed status name still matches.
 *
 * **Reads the WIDE composed fold ({@link foldWide})** — the `immunity-foldwide` widening moved
 * this read-site off the narrow, status-only gather Gap B (`decisions/0171`) had left it on, so a
 * `{kind:"status", name}` grant carried by an equipped system, a pilot effect, or an NPC feature
 * riding `npcFeatureEffectIds` (Aegis's Regenerative Shielding: Immunity to IMPAIRED and SLOWED,
 * RAW p.287) now blocks the add. Before it, such a clause classified `mechanized` nowhere and
 * folded nothing. No attacker-relative status-immunity grant is fielded, so the fold is called
 * self-referentially (`attacker = targetId`) — identical behavior to before for every existing
 * status carrier.
 */
export function statusBlockedByImmunity(args: StatusBlockedByImmunityArgs): boolean {
  const { state, targetId, name, overlay = SEED_OVERLAY } = args;
  const folded = foldWide({ state, targetId, overlay, attacker: targetId });
  const norm = name.trim().toLowerCase();
  // A source-gated scope (Heavy Frame's "by smaller characters") never blocks here: it needs the
  // applying actor, which only {@link smallerCharacterImmunity} reads.
  return folded.immunity.some(
    (scope) => scope.kind === "all" || scope.kind === "damage_and_effects" || (scope.kind === "status" && scope.from === undefined && scope.name.trim().toLowerCase() === norm),
  );
}

/** Args for {@link smallerCharacterImmunity} (max-params-3 discipline, `CODE_STYLE.md`). */
export type SmallerCharacterImmunityArgs = {
  state: EncounterState;
  targetId: string;
  /** The effect's ACTING combatant (the resolution's `self`), or `undefined` when there is none. */
  actorId: string | undefined;
  /** What is being applied: a forced move, or the named status. */
  blocks: "forced_move" | { status: string };
  overlay?: LoadedOverlay;
  /**
   * The event the effect fires on, when it fires on one. A Ram by an actor carrying `ramsLarger`
   * (Synthetic Muscle Netting: "when you … Ram larger targets, you count as the same Size") is never
   * the smaller character, so Heavy Frame doesn't veto that Ram's Prone or push.
   */
  event?: TriggerEvent;
};

/**
 * **Heavy Frame's veto** (the Goliath, Core p.300: "can't be pushed, pulled, knocked Prone, or
 * knocked back by smaller characters"). Returns the id of the entity whose `from:
 * "smaller_character"` immunity scope blocks `blocks` on `targetId`, or `undefined` when nothing
 * does. The scope holds only when `actorId` names a character (`isCharacter`: an object or
 * deployable is not one) other than the target, with a Size below the target's. No acting
 * character, an object, or an equal-or-larger character is never blocked (Open fork F5).
 *
 * Folds each gathered modifier on its own so the refusal can name its entity; conditions and
 * `match` still apply, as in {@link foldWide}. Callers: `interpretForcedMove`,
 * `interpretPlaceAdjacent` and the `add_status` atom (`interpret.ts`), and the bulldoze run-over
 * knockback (`bulldoze.ts`). The grapple drag needs no call: a smaller character is never the
 * dominant party (`grappleDominantOf` ranks by Size), so it never drags a larger one.
 */
export function smallerCharacterImmunity(args: SmallerCharacterImmunityArgs): string | undefined {
  const { state, targetId, actorId, blocks, overlay = SEED_OVERLAY } = args;
  const target = state.combatants[targetId];
  const actor = actorId === undefined ? undefined : state.combatants[actorId];
  if (!target || !actor || actor.id === target.id || !isCharacter(actor) || actor.base.size >= target.base.size) return undefined;
  if (args.event?.family === "attack" && args.event.verb === "ram" && foldWide({ state, targetId: actor.id, overlay, attacker: actor.id }).ramsLarger !== undefined) return undefined;
  const norm = typeof blocks === "string" ? undefined : blocks.status.trim().toLowerCase();
  const holds = (scope: ImmunityScope): boolean =>
    blocks === "forced_move"
      ? scope.kind === "forced_move" && scope.from === "smaller_character"
      : scope.kind === "status" && scope.from === "smaller_character" && scope.name.trim().toLowerCase() === norm;
  const ctx: PredicateContext = { state, actors: { self: targetId, target: targetId, attacker: targetId } };
  for (const m of gatherModifiersAt({ state, carrierId: targetId, overlay })) {
    if (foldModifiers([m], "target", ctx).immunity.some(holds)) return m.name;
  }
  return undefined;
}

/** The warning a {@link smallerCharacterImmunity} veto leaves in the resolution. */
export function smallerCharacterImmunityWarning(args: { state: EncounterState; targetId: string; actorId: string; source: string; what: string }): string {
  const { state, targetId, actorId, source, what } = args;
  const target = state.combatants[targetId]!;
  const actor = state.combatants[actorId]!;
  return `${target.name}: can't be ${what} by a smaller character (${source}) — ${actor.name} is Size ${actor.base.size}, ${target.name} is Size ${target.base.size}; not applied.`;
}

/** Args for {@link alliedCoverCells} (max-params-3 discipline, `CODE_STYLE.md`). */
export type AlliedCoverCellsArgs = {
  state: EncounterState;
  targetId: string;
  attackerId: string;
  overlay?: LoadedOverlay;
};

/**
 * **The Goliath's Guardian** — the footprint cell keys (`world.key`) of every placed, living
 * character that grants hard cover to `targetId` right now: it carries `coverToAllies: "hard"` on
 * its wide fold, is on the target's side (`sideOf`), and is neither the target nor the attacker.
 * The target must be a character ("allied characters").
 * The attack cover query (`foldAttackCover`, and `packages/ai`'s `coverBetween`) passes these to
 * `terrainVisibility` as extra hard-cover cells, so the ordinary line and adjacency geometry
 * decides the degree, exactly as for a hard-cover object of the same footprint.
 */
export function alliedCoverCells(args: AlliedCoverCellsArgs): Set<string> {
  const { state, targetId, attackerId, overlay = SEED_OVERLAY } = args;
  const out = new Set<string>();
  const target = state.combatants[targetId];
  if (!target || !isCharacter(target)) return out;
  const world = worldFor(state);
  for (const g of Object.values(state.combatants)) {
    if (g.id === targetId || g.id === attackerId || g.pos == null || !isCharacter(g) || hasStatus(g.statuses, "Destroyed")) continue;
    if (sideOf(g) !== sideOf(target)) continue;
    if (foldWide({ state, targetId: g.id, overlay, attacker: g.id }).coverToAllies !== "hard") continue;
    for (const cell of world.footprint(g.pos, g.base.size).cells()) out.add(world.key(cell));
  }
  return out;
}

/**
 * **Markerlight's mark** — the entity id of the first modifier on `targetId`'s wide fold carrying
 * `deniesSoftCover`, or `undefined`. An attack on the target then gets no benefit from soft cover
 * (`foldAttackCover`).
 */
export function deniesSoftCover(args: { state: EncounterState; targetId: string; attackerId: string; overlay?: LoadedOverlay }): string | undefined {
  const { state, targetId, attackerId, overlay = SEED_OVERLAY } = args;
  if (state.combatants[targetId] === undefined) return undefined;
  return foldWide({ state, targetId, overlay, attacker: attackerId }).deniesSoftCover;
}

/**
 * **"You count as having soft cover"** — the entity id of the first modifier on `targetId`'s own
 * fold, or one an adjacent ally confers on it, carrying `softCover`, or `undefined`. Folded with no
 * attack event, like {@link deniesSoftCover}; the cover fold (`foldAttackCover`) gates the ranged
 * attack itself.
 */
export function grantsSoftCover(args: { state: EncounterState; targetId: string; attackerId: string; overlay?: LoadedOverlay }): string | undefined {
  const { state, targetId, attackerId, overlay = SEED_OVERLAY } = args;
  if (state.combatants[targetId] === undefined) return undefined;
  const mods = [...gatherModifiersAt({ state, carrierId: targetId, overlay }), ...gatherConferredModifiers(state, targetId, overlay)];
  const ctx: PredicateContext = { state, actors: { self: targetId, target: targetId, attacker: attackerId } };
  return foldModifiers(mods, "target", ctx).softCover;
}

/** Args for {@link hasBlanketImmunity} (max-params-3 discipline, `CODE_STYLE.md`). */
export type HasBlanketImmunityArgs = {
  state: EncounterState;
  targetId: string;
  overlay?: LoadedOverlay;
};

/**
 * The **folded blanket-immunity predicate** (Stasis Generator's "can't be moved, targeted, or
 * affected by any other character or effect"). Does `targetId`
 * hold a `{kind:"all"}` immunity scope right now, off its active overlay modifiers (the Stasis
 * marker status)? This is the read-site the two control-axis scopes need — the movement gate
 * (`interpretForcedMove`, so a blanket-immune target is never force-moved: the demand's
 * "forced_move has no immunity read-site" gap) and the targeting gate (`refereeIntent`, beside
 * the Hidden can't-be-targeted gate).
 *
 * **Deliberately `{kind:"all"}`-only — does NOT honor `{kind:"damage_and_effects"}`.**
 * Stay of Execution's RAW ("Immunity to all damage and effects from
 * external sources") never states Stasis's movement/targeting addendum, and the PDF's own
 * Immunity examples show the two axes are RAW-independent (Biological's all-tech-immune
 * characters "can still be targeted"; movement immunity is always its own explicit named
 * grant, never implied by a bare damage/effects immunity) — so a `damage_and_effects` carrier
 * stays force-movable and targetable by design, not by omission. The status/damage/tech
 * read-sites honor BOTH `all` and `damage_and_effects` inline in their own `.some(...)` (either
 * arm blocks a status/damage/tech), so they don't route through this helper — this one exists
 * for the two read-sites that gate on the fuller Stasis-only clause.
 *
 * **Reads the WIDE composed fold ({@link foldWide})** — moved off the narrow, status-only gather
 * by the `immunity-foldwide` widening, in the same change as {@link immuneToTechAction}/{@link
 * statusBlockedByImmunity}, so a `{kind:"all"}` grant is honored from any carrier vector rather
 * than statuses alone. Self-referential `attacker` (`= targetId`), as for every `immunity`
 * read-site — identical behavior to before for the shipped Stasis marker status.
 */
export function hasBlanketImmunity(args: HasBlanketImmunityArgs): boolean {
  const { state, targetId, overlay = SEED_OVERLAY } = args;
  const folded = foldWide({ state, targetId, overlay, attacker: targetId });
  return folded.immunity.some((scope) => scope.kind === "all");
}

/**
 * Is `targetId` immune to involuntary movement right now? The blanket `{kind:"all"}` scope, or an
 * ungated `{kind:"forced_move"}` one (Black Thumb's rodeo rider: "immune to involuntary movement").
 * A `from`-gated scope (Heavy Frame) is {@link smallerCharacterImmunity}'s, not this.
 */
export function immuneToInvoluntaryMovement(args: HasBlanketImmunityArgs): boolean {
  const { state, targetId, overlay = SEED_OVERLAY } = args;
  const folded = foldWide({ state, targetId, overlay, attacker: targetId });
  return folded.immunity.some((scope) => scope.kind === "all" || (scope.kind === "forced_move" && scope.from === undefined));
}

/**
 * **Does `targetId` pick where someone else's push takes it?** The entity id carrying
 * `chooseInvoluntaryHeading` on the wide fold (Sea Legs, `t_spaceborn:2`), or `undefined`. Read by
 * `interpretForcedMove`.
 */
export function choosesInvoluntaryHeading(args: HasBlanketImmunityArgs): string | undefined {
  const { state, targetId, overlay = SEED_OVERLAY } = args;
  if (state.combatants[targetId] === undefined) return undefined;
  return foldWide({ state, targetId, overlay, attacker: targetId }).chooseInvoluntaryHeading;
}

/** What `actorId`'s held stance does to its attacks (Siege Stabilizers): see {@link attackStance}. */
export type AttackStance = { rangeBonus: number; minimumRange?: number; cannotMelee?: string; ramsLarger?: string };

/**
 * The attack-legality traits `actorId` holds on the wide fold: `rangeBonus` on ranged attacks,
 * `minimumRange` below which it can't make a ranged attack, `cannotMelee` (the entity id that
 * says so), and `ramsLarger` (Ram's Size gate stands down). Read by the attack gates (`checkAttackRange`/`checkAttackCellRange`'s `rangeBonus`,
 * `checkAttackStance`).
 */
export function attackStance(args: HasBlanketImmunityArgs): AttackStance {
  const { state, targetId, overlay = SEED_OVERLAY } = args;
  if (state.combatants[targetId] === undefined) return { rangeBonus: 0 };
  const folded = foldWide({ state, targetId, overlay, attacker: targetId });
  return {
    rangeBonus: folded.rangeBonus,
    ...(folded.minimumRange !== undefined ? { minimumRange: folded.minimumRange } : {}),
    ...(folded.cannotMelee !== undefined ? { cannotMelee: folded.cannotMelee } : {}),
    ...(folded.ramsLarger !== undefined ? { ramsLarger: folded.ramsLarger } : {}),
  };
}

/** Args for {@link actionCostOverride} (max-params-3 discipline, `CODE_STYLE.md`). */
export type ActionCostOverrideArgs = { state: EncounterState; actorId: string; action: string; overlay?: LoadedOverlay };

/**
 * The activation `actorId` holds for core action `action` in place of its catalog cost (Black
 * Thumb 1's Quick Dismount), with the entity id that grants it, or `undefined`. Read on the wide
 * fold by `intentCost`'s catalog branch.
 */
export function actionCostOverride(args: ActionCostOverrideArgs): { activation: "quick" | "free"; source: string } | undefined {
  const { state, actorId, action, overlay = SEED_OVERLAY } = args;
  if (state.combatants[actorId] === undefined) return undefined;
  const held = foldWide({ state, targetId: actorId, overlay, attacker: actorId }).actionCosts.find((a) => a.action === action);
  return held === undefined ? undefined : { activation: held.activation, source: held.source };
}

/** Args for {@link cannotMakeAttacks} (max-params-3 discipline, `CODE_STYLE.md`). */
export type CannotMakeAttacksArgs = {
  state: EncounterState;
  actorId: string;
  overlay?: LoadedOverlay;
};

/**
 * **What stops `actorId` making attacks right now, if anything?** The entity id of the first
 * modifier on the WIDE fold ({@link foldWide} — statuses, pilot/NPC-feature effects, systems)
 * carrying `cannotAttack: true`, or `undefined` when the combatant may attack. The one shipped
 * carrier is the terraforming rig's Unarmed trait (`npcf_unarmed_plant`, `packs/session-one`),
 * which rides `npcFeatureEffectIds`. Read by the room's attack gates, which name the id in the
 * refusal, and by `enumerateActions`, which drops the attack rows, so the two agree.
 */
export function cannotMakeAttacks(args: CannotMakeAttacksArgs): string | undefined {
  const { state, actorId, overlay = SEED_OVERLAY } = args;
  if (state.combatants[actorId] === undefined) return undefined;
  return foldWide({ state, targetId: actorId, overlay, attacker: actorId }).cannotAttack;
}

/** The seed overlay's keyed entries (for tests/loaders that want the map directly). */
export type { OverlayEntry };
